<template>
    <form-group
        v-bind="form_group_props"
    >
        <template v-slot:read_only>
            <slot name="read_only">
                <input :disabled="true" :value="formatted_value" :class="{ [`uk-text-${align}`]: true }" class="uk-width-1-1" />
            </slot>
        </template>
        <template v-slot:label><slot name="label"></slot></template>
        <template v-slot:hint><slot name="hint"></slot></template>
        <input-group
            v-bind="input_group_props"
        >
            <input
                ref="field"
                :id="identifier"
                :type="standardized_type"
                :name="name"
                :value="standardized_value"
                :step="step"
                :min="min"
                :max="max"
                :maxlength="maxlength"
                :placeholder="placeholder"
                autocomplete="off"
                aria-autocomplete="off"
                :aria-describedby="!!has_slot('hint')?`${identifier}-description`:false"
                :disabled="disabled||!editable"
                @focus="maybe_autoselect"
                @keyup.stop="keyup"
                @click.stop
                @blur="blur"
                :class="{ [`uk-width-${width}`]: true, [`uk-text-${align}`]: true, [`uk-form-${size}`]: true, 'uk-form-danger': !!error }"
                :style="fieldStyle"
                class="form-input"
            />
            <slot></slot>
        </input-group>
    </form-group>
</template>

<script>
import debounce from "lodash/debounce"

import is_nibnut_component from "../mixins/IsNibnutComponent"

import FormGroup from "./FormGroup"
import InputGroup from "./InputGroup"

const typed_value = (value, type, default_value = null) => {
    if(type === "float") return parseFloat(value) || default_value
    if(type === "number") return parseInt(value) || default_value
    return value
}

export default {
    name: "FormInput",
    mixins: [is_nibnut_component],
    components: {
        FormGroup,
        InputGroup
    },
    watch: {
        // editable: "maybe_focus_field"
    },
    methods: {
        focus_field () {
            this.$refs.field.focus()
            this.$refs.field.select()
        },
        maybe_focus_field () {
            if(this.editable) setTimeout(this.focus_field, 150)
        },
        maybe_autoselect (event) {
            this.focused = true
            if(this.autoSelect || this.type.match(/^(float|number)$/i)) {
                setTimeout(() => {
                    event.target.select()
                }, 100)
            }
        },
        keyup (event) {
            if(this.inputOnEnter && event && ((event.code === "Enter") || (event.code === "NumpadEnter") || (event.code === "Return"))) {
                if(document.activeElement && document.activeElement.blur) document.activeElement.blur()
                else this.set_date(event.target.name, event)
            }
            this.$emit("keyup", event)
        },
        keydown: debounce(function (event) {
            if(this.live) {
                let value = typed_value(event.target.value)
                if((this.min !== null) && (value < this.min)) value = this.min
                else if((this.max !== null) && (value > this.max)) value = this.max
                this.$emit("input", value, this.name)
            }
        }, 300),
        blur (event) {
            this.focused = false
            let value = typed_value(event.target.value)
            if((this.min !== null) && (value < this.min)) value = this.min
            else if((this.max !== null) && (value > this.max)) value = this.max
            if(!this.live) this.$emit("input", value, this.name)
            this.$emit("blur", value, this.name) // can be useful for autofilled values... Use this to manually set your v-model's value
        }
    },
    computed: {
        form_group_props () {
            return {
                id: this.id,
                name: this.name,
                value: this.value,
                required: this.required,
                editable: this.editable,
                error: this.error,
                waiting: this.saving
            }
        },
        input_group_props () {
            return {
                glyph: this.glyph
            }
        },
        formatted_value () {
            if(!!this.value && this.type.match(/^(float|number)$/i)) return this.$numeral(this.value).format(this.format)
            return this.value
        },
        standardized_value () {
            if(!!this.value && this.focused) return this.value
            return this.formatted_value
        },
        standardized_type () {
            if(this.type.match(/^(float|number)$/i)) return this.focused ? "number" : "text"
            return this.type
        },
        step () {
            if(this.type === "float") return 0.1
            if(this.type === "number") return 1
            return false
        }
    },
    props: {
        id: {
            type: String,
            validator: prop => !!prop
        },
        name: {
            type: String,
            validator: prop => !!prop,
            required: true
        },
        value: {
            default: ""
        },
        type: {
            type: String,
            validator: prop => !!prop && !!prop.match(/^(text|number|float|password|email)$/i),
            default: "text"
        },
        glyph: {
            type: String,
            default: ""
        },
        width: {
            type: String,
            default: "1-1"
        },
        size: {
            type: String,
            validator: prop => !!prop && !!prop.match(/^(small|normal|large)$/i),
            default: "normal"
        },
        align: {
            type: String,
            validator: prop => !!prop && !!prop.match(/^(left|right|center)$/i),
            default: "left"
        },
        fieldStyle: {
            default: false
        },
        required: {
            type: Boolean,
            required: true
        },
        min: {
            type: Number,
            default: null
        },
        max: {
            type: Number,
            default: null
        },
        maxlength: {
            type: Number,
            default: null
        },
        placeholder: {
            type: String,
            default: ""
        },
        format: {
            type: String,
            default: "0,0.00"
        },
        editable: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        autoSelect: {
            type: Boolean,
            default: false
        },
        inputOnEnter: {
            type: Boolean,
            default: false
        },
        live: {
            type: Boolean,
            default: false
        },
        saving: {
            type: Boolean,
            default: false
        },
        error: {
            validator: prop => (typeof prop === "boolean") || (typeof prop === "string"),
            default: ""
        }
    },
    data () {
        return {
            focused: false
        }
    }
}
</script>
